import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useMoney } from '@/lib/money/useMoney.js';
import { CountUp } from './CountUp.js';

interface WinCelebrationProps {
  show: boolean;
  amountMinor: string;
  multiplier: string;
  big?: boolean;
  currency?: string;
  /**
   * Where the amount + multiplier block sits. 'bottom' keeps it clear of a
   * centre-stage result (e.g. the cards scene, where a centred banner would
   * cover the just-revealed card). Defaults to 'center'.
   */
  align?: 'center' | 'bottom';
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rot: number;
  vr: number;
  color: string;
  coin: boolean;
  life: number;
  maxLife: number;
}

const CONFETTI_COLORS = [
  '#f4c542',
  '#ffd700',
  '#ff8a3d',
  '#ff5d8f',
  '#5ad1ff',
  '#8b5cff',
  '#3ddc97',
];
const COIN_COLOR = '#ffcf3f';

const AUTO_HIDE_MS = 2500;

// '1.9439' → '1.94' — the precise multiplier lives in the live-bets table;
// the celebration badge only needs a readable headline figure.
function formatMultiplier(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

function spawnParticles(width: number, height: number, big: boolean, reduced: boolean): Particle[] {
  // The win celebration always plays; reduced-motion only thins the burst.
  const count = reduced ? 40 : big ? 120 : 70;
  const cx = width / 2;
  const cy = height * 0.42;
  const out: Particle[] = [];
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.4;
    const speed = 3 + Math.random() * (big ? 9 : 6);
    const coin = Math.random() < 0.35;
    const maxLife = 60 + Math.random() * 50;
    out.push({
      x: cx + (Math.random() - 0.5) * 40,
      y: cy + (Math.random() - 0.5) * 20,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - (3 + Math.random() * 3),
      size: coin ? 7 + Math.random() * 6 : 5 + Math.random() * 6,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.4,
      color: coin ? COIN_COLOR : CONFETTI_COLORS[i % CONFETTI_COLORS.length]!,
      coin,
      life: 0,
      maxLife,
    });
  }
  return out;
}

function ConfettiCanvas({ big, reduced }: { big: boolean; reduced: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const parent = canvas.parentElement;
    const width = parent?.clientWidth ?? 360;
    const height = parent?.clientHeight ?? 360;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    const gravity = 0.22;
    const drag = 0.99;
    let particles = spawnParticles(width, height, big, reduced);
    let raf = 0;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      let alive = false;
      for (const p of particles) {
        p.life += 1;
        if (p.life > p.maxLife) continue;
        alive = true;
        p.vy += gravity;
        p.vx *= drag;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        const alpha = Math.max(0, 1 - p.life / p.maxLife);
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.coin) {
          ctx.beginPath();
          ctx.ellipse(0, 0, p.size, p.size * 0.95, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = alpha * 0.5;
          ctx.fillStyle = '#fff6d0';
          ctx.beginPath();
          ctx.ellipse(-p.size * 0.25, -p.size * 0.25, p.size * 0.4, p.size * 0.4, 0, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        }
        ctx.restore();
      }
      if (alive) {
        raf = requestAnimationFrame(draw);
      }
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      particles = [];
    };
  }, [big, reduced]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      aria-hidden="true"
    />
  );
}

export function WinCelebration({
  show,
  amountMinor,
  multiplier,
  big = false,
  currency,
  align = 'center',
}: WinCelebrationProps) {
  const { fromMinor } = useMoney(currency);
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!show) {
      setVisible(false);
      return;
    }
    setVisible(true);
    const id = window.setTimeout(() => setVisible(false), AUTO_HIDE_MS);
    return () => window.clearTimeout(id);
  }, [show, amountMinor, multiplier]);

  const target = Number(BigInt(amountMinor || '0'));

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="win-celebration"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className={`pointer-events-none absolute inset-0 z-30 overflow-hidden ${
            align === 'bottom' ? 'flex items-end justify-center pb-6' : 'grid place-items-center'
          }`}
          aria-hidden="true"
        >
          {/* glow stays stage-centred regardless of where the banner sits */}
          <div className="absolute inset-0 grid place-items-center">
            <motion.div
              initial={{ opacity: 0.9, scale: 0.6 }}
              animate={{ opacity: [0.9, 0.55, 0], scale: [0.6, 1.4, 1.7] }}
              transition={{ duration: 1.1, ease: 'easeOut' }}
              className="h-[70%] aspect-square rounded-full"
              style={{
                background:
                  'radial-gradient(circle, var(--primary-glow, var(--primary)) 0%, transparent 65%)',
              }}
            />
          </div>

          <ConfettiCanvas big={big} reduced={reduced ?? false} />

          {/* One cohesive badge on its own blurred layer — never reads as text
              colliding with the scene's result underneath. */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0, y: 12 }}
            animate={
              reduced
                ? { scale: 1, opacity: 1, y: 0 }
                : { scale: [0.5, 1.12, 1], opacity: 1, y: 0 }
            }
            transition={{ duration: 0.55, ease: 'easeOut' }}
            className="relative z-10 flex items-center gap-3 rounded-2xl bg-[var(--bg)]/80 px-5 py-2.5 shadow-[0_10px_36px_rgba(0,0,0,.5)] backdrop-blur-md"
          >
            <CountUp
              value={target}
              durationMs={900}
              format={(n) => `+${fromMinor(BigInt(Math.round(n)))}`}
              className="text-[26px] sm:text-[32px] font-extrabold tracking-tight text-[var(--success)] drop-shadow-[0_0_14px_var(--success)]"
            />
            <motion.span
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.25, type: 'spring', stiffness: 320, damping: 16 }}
              className="rounded-full bg-[var(--primary)]/20 px-2.5 py-1 text-[13px] font-bold text-[var(--primary)]"
            >
              ×{formatMultiplier(multiplier)}
            </motion.span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
